import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Analytics Service
// Tracks user behavior for personalization and UX improvements
// Data is stored securely and not exposed to users

class AnalyticsService {
  constructor() {
    this.eventQueue = [];
    this.sessionId = this.generateSessionId();
    this.sessionStartTime = Date.now();
    this.lastActivityTime = Date.now();
    this.currentPage = null;
    this.pageStartTime = null;
    this.batchInterval = null;
    this.inactivityTimeout = null;
    this.isSessionActive = false;
    
    // Session configuration
    this.BATCH_INTERVAL = 30000; // 30 seconds
    this.INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
    this.MAX_QUEUE_SIZE = 50; // Flush if queue gets too large
  }

  // Initialize analytics tracking
  init() {
    if (this.isSessionActive) return;
    
    this.isSessionActive = true;
    this.trackSession('start');
    
    // Start batch submission interval
    this.batchInterval = setInterval(() => {
      this.flushEvents();
    }, this.BATCH_INTERVAL);
    
    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.flushEvents(); // Flush before tab becomes inactive
      } else {
        this.updateActivity();
      }
    });
    
    // Track before unload (app close)
    window.addEventListener('beforeunload', () => {
      this.trackSession('end');
      this.flushEvents(true); // Synchronous flush
    });
    
    // Start inactivity timer
    this.resetInactivityTimer();
    
    // Track user interactions to reset inactivity
    ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach(event => {
      document.addEventListener(event, () => this.updateActivity(), { passive: true });
    });
  }

  // Generate unique session ID
  generateSessionId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  // Update last activity time and reset inactivity timer
  updateActivity() {
    this.lastActivityTime = Date.now();
    this.resetInactivityTimer();
  }

  // Reset inactivity timer
  resetInactivityTimer() {
    if (this.inactivityTimeout) {
      clearTimeout(this.inactivityTimeout);
    }
    
    this.inactivityTimeout = setTimeout(() => {
      this.handleInactivity();
    }, this.INACTIVITY_TIMEOUT);
  }

  // Handle session inactivity (30 minutes)
  handleInactivity() {
    if (this.isSessionActive) {
      this.trackSession('end');
      this.flushEvents(true);
      this.isSessionActive = false;
      
      // Stop batch interval
      if (this.batchInterval) {
        clearInterval(this.batchInterval);
        this.batchInterval = null;
      }
    }
  }

  // Track session start/end
  trackSession(action) {
    const event = {
      event_type: action === 'start' ? 'session_start' : 'session_end',
      event_name: action === 'start' ? 'app_opened' : 'app_closed',
      page: window.location.pathname,
      metadata: {
        duration_seconds: action === 'end' ? Math.floor((Date.now() - this.sessionStartTime) / 1000) : 0
      },
      session_id: this.sessionId,
      timestamp: new Date().toISOString(),
      device_info: this.getDeviceInfo()
    };
    
    this.queueEvent(event);
  }

  // Track page view
  trackPageView(page) {
    // Calculate duration on previous page
    if (this.currentPage && this.pageStartTime) {
      const duration = Math.floor((Date.now() - this.pageStartTime) / 1000);
      this.queueEvent({
        event_type: 'page_view',
        event_name: 'page_duration',
        page: this.currentPage,
        metadata: { duration_seconds: duration },
        session_id: this.sessionId,
        timestamp: new Date().toISOString(),
        device_info: this.getDeviceInfo()
      });
    }
    
    // Track new page view
    this.currentPage = page;
    this.pageStartTime = Date.now();
    
    this.queueEvent({
      event_type: 'page_view',
      event_name: 'page_visited',
      page,
      metadata: {},
      session_id: this.sessionId,
      timestamp: new Date().toISOString(),
      device_info: this.getDeviceInfo()
    });
    
    this.updateActivity();
  }

  // Track feature usage
  trackFeature(featureName, metadata = {}) {
    this.queueEvent({
      event_type: 'feature_used',
      event_name: featureName,
      page: window.location.pathname,
      metadata,
      session_id: this.sessionId,
      timestamp: new Date().toISOString(),
      device_info: this.getDeviceInfo()
    });
    
    this.updateActivity();
  }

  // Track meal interaction
  trackMealInteraction(action, mealData) {
    this.queueEvent({
      event_type: 'meal_interaction',
      event_name: action, // 'meal_selected', 'meal_swapped', 'meal_favorited', 'meal_ordered'
      page: window.location.pathname,
      metadata: mealData,
      session_id: this.sessionId,
      timestamp: new Date().toISOString(),
      device_info: this.getDeviceInfo()
    });
    
    this.updateActivity();
  }

  // Track insight interaction
  trackInsightInteraction(action, insightData) {
    this.queueEvent({
      event_type: 'insight_interaction',
      event_name: action, // 'insight_viewed', 'insight_feedback_given', 'insight_generated'
      page: window.location.pathname,
      metadata: insightData,
      session_id: this.sessionId,
      timestamp: new Date().toISOString(),
      device_info: this.getDeviceInfo()
    });
    
    this.updateActivity();
  }

  // Track support interaction
  trackSupportInteraction(action, supportData) {
    this.queueEvent({
      event_type: 'support_interaction',
      event_name: action, // 'ticket_created', 'help_accessed', 'faq_searched'
      page: window.location.pathname,
      metadata: supportData,
      session_id: this.sessionId,
      timestamp: new Date().toISOString(),
      device_info: this.getDeviceInfo()
    });
    
    this.updateActivity();
  }

  // Track premium feature click
  trackPremiumClick(feature, metadata = {}) {
    this.queueEvent({
      event_type: 'premium_interaction',
      event_name: feature, // 'upgrade_modal_viewed', 'upgrade_clicked', 'premium_feature_locked'
      page: window.location.pathname,
      metadata,
      session_id: this.sessionId,
      timestamp: new Date().toISOString(),
      device_info: this.getDeviceInfo()
    });
    
    this.updateActivity();
  }

  // Queue event for batch submission
  queueEvent(event) {
    this.eventQueue.push(event);
    
    // Flush if queue is getting large
    if (this.eventQueue.length >= this.MAX_QUEUE_SIZE) {
      this.flushEvents();
    }
  }

  // Flush queued events to backend
  async flushEvents(synchronous = false) {
    if (this.eventQueue.length === 0) return;
    
    const eventsToSend = [...this.eventQueue];
    this.eventQueue = [];
    
    const payload = { events: eventsToSend };
    
    try {
      if (synchronous) {
        // Use sendBeacon for synchronous send on page unload
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        navigator.sendBeacon(`${API}/analytics/track`, blob);
      } else {
        // Use axios for async send
        await axios.post(`${API}/analytics/track`, payload, {
          withCredentials: true
        });
      }
    } catch (error) {
      console.error('Failed to send analytics:', error);
      // Re-queue events on failure (optional)
      // this.eventQueue.unshift(...eventsToSend);
    }
  }

  // Get device/browser info
  getDeviceInfo() {
    return {
      user_agent: navigator.userAgent,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
  }

  // Cleanup
  destroy() {
    if (this.batchInterval) {
      clearInterval(this.batchInterval);
    }
    if (this.inactivityTimeout) {
      clearTimeout(this.inactivityTimeout);
    }
    this.flushEvents(true);
    this.isSessionActive = false;
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

export default analyticsService;
